import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronUpIcon,
  LockClosedIcon,
  QrCodeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ShiftDetailInvite, ShiftDetailWorker } from '../api/types';
import useShiftDetail from '../hooks/useShiftDetail';
import { shiftIdentity } from '../util/identity';

import ActivityItem from './ActivityItem';
import DependabilityGateIcon from './DependabilityGateIcon';
import Avatar from './Avatar';
import InvitedWorkersModal from './InvitedWorkersModal';
import RecentActivityModal from './RecentActivityModal';

import ShareQRCodeModal from '@/components/ShareQRCodeModal';
import ShiftQRCodeModal from '@/components/ShiftQRCodeModal';
import { GetJobQuery } from '@/types/graphql';

// How many activity rows the panel previews before "View all" takes over.
// Figma 39341:54060 draws five; the API's own cap is higher, so this is the
// authority for what the PANEL shows.
const ACTIVITY_PREVIEW = 5;

// Client first, then the optional venue label. Never the billing account. The
// same helper the calendar cards title on, so the card and the panel it opens
// always name the same thing (GRAVY-10340).
const panelIdentity = shiftIdentity;

// Shift Detail side-panel, opened when a shift card is clicked. Docked on the
// right; the X (or a click on the backdrop) closes it. Pixel-transcribed from
// the Figma (Dashboard-shift-details).

const clockRange = (
  start?: string | null,
  end?: string | null,
  tz?: string | null,
): string => {
  if (!start || !end) return '';
  const fmt = (iso: string) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(iso));
  return `${fmt(start)} - ${fmt(end)}`;
};

// Why the Quality Gate reached its verdict, in Ops language. Hover only — the
// row's visible layout is unchanged (GRAVY-10313). Anything not listed (null,
// i.e. the gate is not in force) gets no tooltip rather than a misleading one.
const GATE_REASON_LABELS: Record<string, string> = {
  eligible: 'Meets this shift’s Dependability gate',
  insufficient_shifts:
    'Below this shift’s Dependability gate: not enough completed shifts',
  active_violation:
    'Below this shift’s Dependability gate: has an active violation',
  lta_exempt: 'Exempt: long-term assignment worker for this client',
  favorite_exempt: 'Exempt: favourited worker for this client',
};

/**
 * A collapsible worker list section — "Invites (10)", "Hired (1)".
 *
 * Figma 39341:54060 splits the panel's people into two lists because they are
 * two different questions: who was ASKED, and who is ON the shift. The count
 * lives in the heading so it is legible while collapsed.
 *
 * Both sections render at zero (GRAVY-10328 retest): "Invites (0)" above an
 * empty-state line is the answer to "who was asked", and it used to be omitted
 * entirely — which reads as a panel that failed to load the section rather than
 * a shift nobody was invited to.
 *
 * Search and the filter control are in the frame but explicitly EXCLUDED by the
 * ticket, so they are not built here.
 */
const PanelSection = ({
  label,
  count,
  empty,
  children,
}: {
  label: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  const id = useId();

  return (
    <section
      className="dashv2-panel-section"
      data-testid={`panelSection-${label}`}
    >
      <button
        type="button"
        className="dashv2-panel-sectionhead"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="dashv2-panel-sectiontitle">
          {label} ({count})
        </span>
        <ChevronUpIcon
          style={{ transform: open ? 'none' : 'rotate(180deg)' }}
        />
      </button>
      <div id={id} hidden={!open}>
        {count === 0 ? <p className="dashv2-panel-empty">{empty}</p> : children}
      </div>
    </section>
  );
};

/**
 * The Quality Gate verdict for one person, as the approved design draws it: a
 * boxed green ↑ (meets the gate) or red ↓ (below it), and nothing else.
 *
 * The numeric score is deliberately NOT on the row any more — Figma 39341:54060
 * shows the indicator alone — but it is not lost either: it rides in the
 * tooltip with the gate reason, so the authoritative value is still reachable
 * without putting a number beside an arrow that does not derive from it.
 *
 * Renders NOTHING when there is no verdict — an unscored worker, or a shift
 * whose gate is off (`unevaluated`). An arrow in either case would assert a
 * judgement the server never made, and the ticket is explicit that
 * Dependability is omitted rather than faked when the gate is not enabled.
 */
const DependabilityIndicator = ({
  tier,
  belowGate,
  score,
  reason,
}: {
  tier?: string | null;
  belowGate?: boolean | null;
  score?: number | null;
  reason?: string | null;
}) => {
  if (!tier || tier === 'unevaluated' || belowGate == null) return null;

  const explanation = GATE_REASON_LABELS[reason ?? ''];
  const title = [score != null ? `Dependability: ${score}` : null, explanation]
    .filter(Boolean)
    .join(' — ');

  return (
    <span
      className={`dashv2-panel-dep is-${tier}${belowGate ? ' is-below-gate' : ''}`}
      title={title || undefined}
      aria-label={
        belowGate
          ? 'Below this shift’s Dependability gate'
          : 'Meets this shift’s Dependability gate'
      }
    >
      {belowGate ? <ArrowDownIcon /> : <ArrowUpIcon />}
    </span>
  );
};

/**
 * One invited worker. Kept distinct from `WorkerRow` because the two lists come
 * from different tables — `job_offers` is who was asked, `job_workers` is who is
 * on the shift — but they render the same indicator, resolved from the same
 * policy, so one worker cannot be described differently by the two lists.
 */
const InviteRow = ({ invite }: { invite: ShiftDetailInvite }) => (
  <div className="dashv2-panel-worker" data-testid="inviteRow">
    <Avatar name={invite.name} src={invite.avatar_url} />
    <div className="dashv2-panel-workermeta">
      <span className="dashv2-panel-workername">
        {invite.name || 'Unknown worker'}
      </span>
      <div className="dashv2-panel-workertags">
        <DependabilityIndicator
          tier={invite.tier}
          belowGate={invite.below_gate}
          score={invite.dependability_score}
        />
        {/* Whatever invitation state the server reports — Invited and Declined
            are the two the design draws, but the chip names the authoritative
            value rather than collapsing anything unrecognised into a generic
            fallback (GRAVY-10328). */}
        <span
          className={`dashv2-panel-status is-${invite.status}`}
          data-status={invite.status}
        >
          {invite.status === 'declined' && <InformationCircleIcon />}
          {invite.status_label}
        </span>
      </div>
    </div>
  </div>
);

const WorkerRow = ({ worker }: { worker: ShiftDetailWorker }) => (
  <div className="dashv2-panel-worker" data-testid="workerRow">
    <Avatar name={worker.name} src={worker.avatar_url} />
    <div className="dashv2-panel-workermeta">
      <span className="dashv2-panel-workername">
        {worker.name || 'Unknown worker'}
      </span>
      <div className="dashv2-panel-workertags">
        <DependabilityIndicator
          tier={worker.tier}
          belowGate={worker.below_gate}
          score={worker.dependability_score}
          reason={worker.gate_reason}
        />
        {/* The worker's CURRENT state (GRAVY-10326). This used to be a "Hired"
            chip shown whenever `hired` was true — which the API hardcoded,
            because the roster could only contain workers whose status was
            literally 'hired'. Everyone else was missing from the panel
            entirely, so a finished shift read "3 of 3 hired" above one row.

            The roster is now every assigned worker who was not dropped or
            dismissed, so the chip has to name the state rather than assert one.
            Falls back to the raw status if the server sends one this build has
            no label for, and renders nothing at all when there is no status —
            better than inventing "Hired" for a row that is not. */}
        {(worker.status_label || worker.status) && (
          <span
            className={`dashv2-panel-hiredtag is-${worker.status ?? 'unknown'}`}
          >
            {worker.status_label || worker.status}
          </span>
        )}
      </div>
    </div>
  </div>
);

type Props = {
  agencyId: number;
  shiftId: number;
  onClose: () => void;
};

const ShiftDetailPanel = ({ agencyId, shiftId, onClose }: Props) => {
  // `refetch` so a hire made from the invited-worker list updates the roster
  // and the counts beside it, rather than leaving the panel showing the state
  // from before the action (GRAVY-10350 C).
  const { data, isLoading, isError, refetch } = useShiftDetail(
    agencyId,
    shiftId,
  );
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  // "View all" opens the full activity history over the panel; the panel stays
  // mounted underneath, so closing the modal returns to this exact shift with
  // its scroll position, the selected week and every filter untouched.
  const [activityOpen, setActivityOpen] = useState(false);
  // The group behind an activity row's "N workers" (GRAVY-10345). Held here
  // rather than inside the row so the SAME list opens from the panel preview
  // and from the full-history modal.
  const [invited, setInvited] = useState<{
    ids: number[];
    names: string[];
  } | null>(null);

  // A worker's name in the feed opens their profile — the same destination the
  // queue cards use, so "clicking a user" means one thing across the dashboard.
  // A full navigation, deliberately: the profile is a page, not a panel.
  const openWorker = (workerId: number) => {
    window.open(`/workers_v2/${workerId}`, '_blank', 'noopener');
  };
  const openInvited = (ids: number[], names: string[]) =>
    setInvited({ ids, names });
  const viewAllRef = useRef<HTMLButtonElement>(null);
  // "View QR" opens the established Shift QR experience over the panel, and
  // "Share QR Code" inside it swaps to the share modal — the same two-step flow
  // Job Detail's ShiftQRButton drives (GRAVY-10322). It used to be a <Link> to
  // the SAME href as "View job", so both actions navigated to Job Detail and the
  // admin thought they had opened a QR they never saw.
  const [qrView, setQrView] = useState<null | 'qr' | 'share'>(null);
  // COLLAPSED by default (GRAVY-10342). The design opened this block expanded,
  // but in use the panel is opened to work the roster — who is hired, who was
  // invited, what just happened — and the client/address/supervisor block push
  // all of that below the fold. Ops asked for it shut on open; "Show info" is
  // one click away and the state is per-open, not sticky, so a panel always
  // opens the same way rather than depending on what was done last time.
  const [infoOpen, setInfoOpen] = useState(false);
  const infoId = useId();
  const viewQrRef = useRef<HTMLButtonElement>(null);
  // Read on mount, before focus moves into the panel, so it survives however
  // the panel is dismissed.
  const openerRef = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );

  // Escape closes the panel, matching the X. Bound on document rather than the
  // panel so it works before focus has entered it.
  //
  // While the activity modal is open the panel stands down entirely: both
  // listeners sit on document, so stopPropagation in the modal cannot suppress
  // this one and a single Escape would otherwise close the modal AND the panel,
  // dropping the admin back on the calendar (GRAVY-10294).
  useEffect(() => {
    if (activityOpen || qrView) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, activityOpen, qrView]);

  // A different shift takes over the same mounted panel, so an open modal would
  // otherwise keep showing the previous shift's history over the new panel.
  useEffect(() => {
    setActivityOpen(false);
    // Same reasoning for the QR: a QR left open while the panel switched shifts
    // would show the PREVIOUS occurrence's code over the new panel, which is the
    // one failure mode that actually matters here — a wrong code gets scanned.
    setQrView(null);
  }, [shiftId]);

  // Move focus to the close button on open, and hand it back to the card that
  // opened the panel on close — otherwise a keyboard user is returned to the
  // top of the document. The card carries a stable id from ShiftCard; the
  // element active at mount is the fallback, since Safari does not focus a
  // button on click.
  useEffect(() => {
    closeRef.current?.focus();
    const opener = openerRef.current;
    return () => {
      const card = document.getElementById(`dashv2-shift-${shiftId}`);
      const target = card ?? opener;
      if (target instanceof HTMLElement && document.contains(target)) {
        target.focus();
      }
    };
  }, [shiftId]);

  const jobHref = data?.order_id
    ? `/orders/${data.order_id}/jobs/${data.job_id}`
    : '#';
  const address = data
    ? [
        data.address_line1,
        [data.city, data.state, data.zip].filter(Boolean).join(', '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  // The header renders in every state — loading, error and loaded — so the
  // close button is never conditionally omitted and focus always has somewhere
  // to land. Only the client identity waits for data.
  //
  // Figma 39341:54060. The header is the Needs attention badge and the close
  // control on one row, then the SKILL as the heading. That pair is the sticky
  // band: everything below it scrolls under it.
  //
  // NEEDS ATTENTION is the calendar card's red Critical band, said in words
  // (GRAVY-10328). It comes from the server's `fill_status` — the same shared
  // classification the card runs — rather than being re-derived here, so a red
  // card cannot open a calm panel. A non-critical shift shows no badge at all,
  // and the close control keeps its position either way.
  //
  // The lifecycle status (Upcoming / In progress / Completed) used to live in
  // this row; the updated design moves it into the tag block below Job ID,
  // which is what frees the row for the badge.
  //
  // The CLIENT LOGO is gone (GRAVY-10328 retest): the panel is opened from a
  // card that already named the client, and the client is a labelled field
  // below. That reverses GRAVY-10292, which existed because the logo painted
  // under the navbar — removing the element removes that failure mode with it.
  //
  // The heading falls back to the client identity when a job carries no role,
  // so it is never empty and `aria-labelledby` always resolves.
  const header = (
    <div className="dashv2-panel-header">
      <div className="dashv2-panel-headrow">
        {data?.fill_status === 'critical' ? (
          <span className="dashv2-panel-critical" data-testid="needsAttention">
            Needs attention
          </span>
        ) : (
          <span />
        )}
        <button
          ref={closeRef}
          type="button"
          className="dashv2-panel-close"
          aria-label="Close shift details"
          onClick={onClose}
        >
          <XMarkIcon />
        </button>
      </div>
      {data && (
        <h2
          className="dashv2-panel-title"
          id={titleId}
          title={data.role || panelIdentity(data)}
        >
          {data.role || panelIdentity(data)}
        </h2>
      )}
    </div>
  );

  // "View job" and, for QR jobs only, "View QR" — pinned to the bottom of the
  // panel by the design (GRAVY-10328). They used to sit at the TOP, directly
  // under the header, which pushed the roster down and put the two actions
  // furthest from the content an admin reads before using them.
  //
  // Rendered outside the scrolling body, so the bar cannot cover the last
  // roster or activity row: the body is the flex child that scrolls, and the
  // footer takes its own space beneath it.
  const footer = data ? (
    <div className="dashv2-panel-footer" data-testid="panelFooter">
      <Link className="dashv2-panel-btn is-primary" to={jobHref}>
        View job
      </Link>
      {/* Only for jobs that actually verify check-in by QR. This rendered
          unconditionally, offering QR for jobs that do not support it
          (GRAVY-10293). The test is `=== true` on purpose: an older backend
          omits qr_enabled entirely, and undefined must read as "no QR", never
          as "assume yes". */}
      {data.qr_enabled === true && (
        <button
          type="button"
          className="dashv2-panel-btn is-secondary"
          ref={viewQrRef}
          onClick={(e) => {
            // Focus explicitly BEFORE the dialog mounts. MUI restores focus to
            // whatever was active when it opened, and that would otherwise be
            // the panel's own close button (the panel focuses it on mount) — so
            // the admin would be returned to "close Shift Detail" after looking
            // at a QR code. Safari also does not focus a button on click.
            e.currentTarget.focus();
            setQrView('qr');
          }}
        >
          View QR
          <QrCodeIcon />
        </button>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="dashv2-panel-overlay" onClick={onClose} />
      <aside
        className="dashv2-panel"
        role="dialog"
        aria-modal="true"
        // Named by the client heading once it has loaded; the static label
        // covers the loading and error states, where there is no heading yet.
        {...(data
          ? { 'aria-labelledby': titleId }
          : { 'aria-label': 'Shift detail' })}
      >
        {header}
        {isLoading && !data ? (
          <div className="dashv2-panel-loading">Loading…</div>
        ) : isError || !data ? (
          <div className="dashv2-panel-loading">
            Unable to load shift detail.
          </div>
        ) : (
          <div className="dashv2-panel-body">
            {/* Details */}
            <div className="dashv2-panel-details">
              <div className="dashv2-panel-jobblock">
                {/* Figma 39341:54060 orders this shift time + total hours, THEN
                    Job ID — the shift is what the panel is about; the id is a
                    reference. */}
                <div className="dashv2-panel-shiftinfo">
                  <div className="dashv2-panel-hours">
                    <span>
                      {clockRange(data.start_at, data.end_at, data.timezone)}
                    </span>
                    {data.duration_hours != null && (
                      <>
                        <span className="dashv2-panel-dot" />
                        <span>{data.duration_hours} hrs</span>
                      </>
                    )}
                  </div>
                </div>

                <Link className="dashv2-panel-jobid" to={jobHref}>
                  Job ID: {data.job_id}
                  <ArrowTopRightOnSquareIcon />
                </Link>
              </div>

              {/* JOB TAGS (GRAVY-10328). Every tag here is conditional and
                  present only when it applies — the block is a description of
                  this job, so a tag that renders unconditionally is a claim
                  about a job that does not carry it. They wrap rather than
                  scroll, which is what keeps them inside a 330px rail. */}
              <div className="dashv2-panel-gaterow" data-testid="panelTags">
                {/* Private = an UNPOSTED job, i.e. a restricted worker pool —
                    the same flag the calendar card draws as a lock. It is not
                    LTA, despite `Job#private?` on the model returning exactly
                    that; the two are independent tags and a job may carry
                    either, both or neither. */}
                {data.restricted_pool === true && (
                  <span className="dashv2-panel-chip" data-testid="tagPrivate">
                    <LockClosedIcon />
                    Private
                  </span>
                )}
                {data.job_type === 'LTA' && (
                  <span className="dashv2-panel-chip" data-testid="tagLta">
                    LTA
                  </span>
                )}
                {/* Shown ONLY while the gate is in force. This used to render a
                    muted "Dependability gate: Off" chip so a client with no gate
                    stayed distinguishable from a panel that failed to load one —
                    the updated design rules that out explicitly: no "Not
                    enabled" tag, the tag is absent instead. The level (Strict /
                    Relaxed) is the server's, never inferred here. */}
                {data.dependability_gate_enabled === true && (
                  <span className="dashv2-panel-chip" data-testid="tagGate">
                    {/* The approved design-system badge (GRAVY-10328), exported
                        from Figma; the Trophy it replaces was a stand-in. */}
                    <DependabilityGateIcon />
                    Dependability gate: {data.dependability_gate_label}
                  </span>
                )}
                {data.bg_check_required === true && (
                  <span className="dashv2-panel-chip" data-testid="tagBgCheck">
                    BG Check
                  </span>
                )}
                {data.worker_classification && (
                  <span className="dashv2-panel-chip">
                    {data.worker_classification}
                  </span>
                )}
                {/* The shift's lifecycle status — Upcoming / In progress /
                    Completed. It was the first thing in the sticky header until
                    the updated design moved it here, into the tag block, which
                    is what frees the header row for the Needs attention badge. */}
                {data.status_label && (
                  <span
                    className="dashv2-panel-lifecycle"
                    data-testid="tagLifecycle"
                  >
                    {data.status_label}
                  </span>
                )}
              </div>

              {/* Client / Address / Shift supervisor are labelled blocks in the
                  updated design, and collapse together behind "Hide info".
                  Collapsing is presentation only — nothing here is refetched —
                  so the roster and activity stay put while it toggles. */}
              <div
                className="dashv2-panel-infoblock"
                id={infoId}
                hidden={!infoOpen}
              >
                <div className="dashv2-panel-field">
                  <span className="dashv2-panel-fieldlabel">Client</span>
                  <span className="dashv2-panel-fieldvalue">
                    {panelIdentity(data)}
                  </span>
                </div>

                {address && (
                  <div className="dashv2-panel-field">
                    <span className="dashv2-panel-fieldlabel">Address</span>
                    <span className="dashv2-panel-fieldvalue">{address}</span>
                  </div>
                )}

                {data.supervisor?.name && (
                  <div className="dashv2-panel-supervisor">
                    <span className="dashv2-panel-suptitle">
                      Shift supervisor
                    </span>
                    <span className="dashv2-panel-supname">
                      {data.supervisor.name}
                    </span>
                    {data.supervisor.phone && (
                      <span className="dashv2-panel-supphone">
                        {data.supervisor.phone}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="dashv2-panel-infotoggle"
                aria-expanded={infoOpen}
                aria-controls={infoId}
                onClick={() => setInfoOpen((o) => !o)}
              >
                {infoOpen ? 'Hide info' : 'Show info'}
                <ChevronUpIcon
                  style={{ transform: infoOpen ? 'none' : 'rotate(180deg)' }}
                />
              </button>

              <div className="dashv2-panel-divider" />

              <div>
                {/* Two lists, not one (GRAVY-10328): who was ASKED and who is
                    ON the shift. Both render at zero — the count is part of the
                    answer, and an omitted section reads as one that failed to
                    load. The old "N of M hired · X% filled" row above them is
                    gone: the two counts say the same thing, and the design
                    carries neither. */}
                <PanelSection
                  label="Invites"
                  count={(data.invites ?? []).length}
                  empty="Nobody has been invited to this shift."
                >
                  {(data.invites ?? []).map((i) => (
                    <InviteRow key={i.worker_id ?? i.name} invite={i} />
                  ))}
                </PanelSection>

                <PanelSection
                  label="Hired"
                  count={(data.workers ?? []).length}
                  empty="Nobody is hired for this shift yet."
                >
                  {(data.workers ?? []).map((w) => (
                    <WorkerRow key={w.worker_id ?? w.name} worker={w} />
                  ))}
                </PanelSection>
                {/* No "Increase pay rate" action here: the workflow is out of
                    scope for this release, and the button shipped as an enabled
                    CTA that did nothing when clicked (GRAVY-10294). Product
                    confirmed it is removed rather than disabled — a greyed-out
                    control still advertises a feature that does not exist. Do
                    not wire it to a legacy or temporary flow; a future
                    enhancement restores it deliberately. */}
              </div>
            </div>

            {/* Recent activity — a PREVIEW, capped at the number the design
                shows. The API sends more than fits, so the cap is applied here
                rather than relying on the server's own limit; "View all" is the
                way to the rest of the history.

                The section renders even with nothing in it, so a shift with no
                events reads as quiet rather than as a panel missing a block.
                "View all" is withheld in that case: a modal that can only ever
                be empty is not an action. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="dashv2-panel-actitle">
                Recent activity
                {/* A real button, not the styled span this shipped as: the
                    span had no handler and no keyboard affordance, so
                    clicking it did nothing at all (GRAVY-10294). */}
                {(data.recent_activity ?? []).length > 0 && (
                  <button
                    ref={viewAllRef}
                    type="button"
                    className="dashv2-panel-viewall"
                    onClick={() => setActivityOpen(true)}
                  >
                    View all
                  </button>
                )}
              </div>
              {(data.recent_activity ?? []).length === 0 ? (
                <p className="dashv2-panel-empty">No recent activity.</p>
              ) : (
                <div className="dashv2-panel-actlist">
                  {(data.recent_activity ?? [])
                    .slice(0, ACTIVITY_PREVIEW)
                    .map((item) => (
                      <ActivityItem
                        key={item.id}
                        item={item}
                        onSelectWorker={openWorker}
                        onSelectWorkers={openInvited}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
        {footer}
      </aside>

      {activityOpen && (
        <RecentActivityModal
          agencyId={agencyId}
          shiftId={shiftId}
          onSelectWorker={openWorker}
          onSelectWorkers={openInvited}
          onClose={() => {
            setActivityOpen(false);
            // Focus goes back to the control that opened the modal rather than
            // to the top of the document.
            viewAllRef.current?.focus();
          }}
        />
      )}

      {invited && (
        <InvitedWorkersModal
          names={invited.names}
          workerIds={invited.ids}
          // The panel has already fetched these, so opening the list costs no
          // request (GRAVY-10350 C) — and since GRAVY-10355 they are the list
          // itself, not just decoration on the event's frozen ids.
          invites={data?.invites}
          // Who is actually ON the shift, so the list can refuse a Hire the
          // server would reject anyway (GRAVY-10355 item 3).
          roster={data?.workers}
          jobId={data?.job_id}
          onSelectWorker={openWorker}
          onHired={() => refetch?.()}
          onClose={() => setInvited(null)}
        />
      )}

      {/* The ESTABLISHED Shift QR experience, reused rather than reimplemented
          (GRAVY-10322). It needs only `job.id`: skill, address, supervisor,
          shifts and the verification codes all come from its own
          `/api/jobs/:id` REST call, so Dashboard V2 stays REST-only and adds no
          GraphQL query. The prop is GraphQL-shaped, hence the cast — nothing
          beyond `id` is read when `jobDetails` is not supplied.

          `initialShiftId` is what makes this THIS shift's QR rather than
          whichever of the job's shifts happens to be first.

          Mounted only while open and keyed on the job, so a code can never be
          rendered for a job other than the one currently in the panel. */}
      {qrView && data?.job_id != null && (
        <>
          <ShiftQRCodeModal
            key={`qr-${data.job_id}`}
            isOpen={qrView === 'qr'}
            job={{ id: String(data.job_id) } as GetJobQuery['job']}
            initialShiftId={shiftId}
            onShareClick={() => setQrView('share')}
            onClose={() => {
              setQrView(null);
              viewQrRef.current?.focus();
            }}
          />
          <ShareQRCodeModal
            key={`share-${data.job_id}`}
            isOpen={qrView === 'share'}
            job={{ id: String(data.job_id) } as GetJobQuery['job']}
            onShare={() => setQrView(null)}
            onClose={() => {
              setQrView(null);
              viewQrRef.current?.focus();
            }}
          />
        </>
      )}
    </>
  );
};

export default ShiftDetailPanel;
